import Header from "@components/Header";
import { getTheme, Themes } from "@components/Header/ThemeSelector";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { surveyThemeDark, surveyThemeLight } from "./SurveyTheme";
import "./SurveyStyles.css";
import { useGetCourseQuery } from "@api/course";
import { useGetSurveyQuery, usePostSurveyMutation } from "@api/survey";
import LoadingSpinner from "@components/LoadingSpinner";
import { useToastEffect } from "@hooks/hooks";

const Survey = lazy(() =>
  import("survey-react-ui").then((module) => ({ default: module.Survey })),
);

function SurveyPage() {
  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("Course ID is required");
  }

  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["surveyPage"]);
  const [themeState, setThemeState] = useState(getTheme());
  const [survey, setSurvey] = useState<InstanceType<
    typeof import("survey-core")["Model"]
  > | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const { data: course } = useGetCourseQuery(courseId);
  const { data: surveyData, isLoading } = useGetSurveyQuery(courseId);
  const [postSurvey, { isError, error, isSuccess }] = usePostSurveyMutation();

  useToastEffect({
    isError,
    error,
    isSuccess,
    successMessage: t("surveyPage.surveySubmissionSuccess", {
      ns: "toast",
    }),
  });

  const localStorageKey = `${courseId}-surveyData`;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    (async () => {
      const [{ Model }, { marked }] = await Promise.all([
        import("survey-core"),
        import("marked"),
      ]);
      await import("survey-core/survey-core.min.css");
      await import("survey-core/i18n/german");

      const model = new Model(surveyData?.survey);
      model.locale = i18n.language;
      model.setValue("firstSubmission", !surveyData?.hasFilledOutAnySurvey);

      const saved = localStorage.getItem(localStorageKey);
      if (saved) {
        model.data = JSON.parse(saved);
      }

      model.onValueChanged.add((sender) => {
        localStorage.setItem(localStorageKey, JSON.stringify(sender.data));
      });

      model.onComplete.add((sender) => {
        postSurvey({
          courseId,
          submission: sender.data,
        });
        localStorage.removeItem(localStorageKey);
        setSubmitted(true);
      });

      model.onTextMarkdown.add((_, options) => {
        const doc = new DOMParser().parseFromString(
          marked(options.text) as string,
          "text/html",
        );
        const body = doc.body;

        if (
          body.childElementCount === 1 &&
          body.firstElementChild?.tagName.toLowerCase() === "p"
        ) {
          options.html = body.firstElementChild?.innerHTML;
        } else {
          options.html = body.innerHTML;
        }
      });

      setSurvey(model);
    })();
  }, [
    surveyData,
    courseId,
    isLoading,
    localStorageKey,
    i18n.language,
    postSurvey,
  ]);

  useEffect(() => {
    if (!survey) {
      return;
    }
    survey.applyTheme(
      themeState === Themes.dark ? surveyThemeDark : surveyThemeLight,
    );
  }, [themeState, survey]);

  useEffect(() => {
    if (!survey) {
      return;
    }
    survey.locale = i18n.language;
  }, [i18n.language, survey]);

  useEffect(() => {
    const handleThemeChange = () => setThemeState(getTheme());
    window.addEventListener("themeChange", handleThemeChange);
    return () => window.removeEventListener("themeChange", handleThemeChange);
  }, []);

  if (!survey) {
    return null;
  }

  return (
    <Header
      backButtonRoute={"/"}
      breadcrumbs={[
        {
          name:
            course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
        },
        {
          name: t("breadcrumbSurvey", { ns: "header" }),
        },
      ]}
    >
      <div className="flex justify-center">
        <div className="mt-4 w-full max-w-4xl md:mx-4">
          {!submitted && surveyData ? (
            <Suspense fallback={<LoadingSpinner />}>
              <Survey model={survey} />
            </Suspense>
          ) : (
            <div className="mt-12 flex flex-col items-center justify-center">
              <div className="font-bold text-2xl">{t("surveySubmitted")}</div>
              <button
                type="button"
                className="btn btn-primary mt-4"
                onClick={() => navigate("..")}
              >
                {t("surveyGoToCourse")}
              </button>
            </div>
          )}
        </div>
      </div>
    </Header>
  );
}

export default SurveyPage;
